/**
 * Sonar (PRD §14/§15). Passive sonar continuously paints the terrain profile on
 * the HUD. An active ping is a shell that physically travels outward, lighting
 * whatever it touches — and telling nearby drones exactly where you are.
 */

import { hyp } from '../core/math';
import { SONAR } from './config';

export type ContactKind =
  'terrain' | 'creature' | 'mechanical' | 'objective' | 'unknown'
  /**
   * An ore deposit. Its own kind rather than an `objective`, because the two
   * answer different questions — an objective is somewhere you have to go, a
   * seam is somewhere you might choose to. Painting them the same amber made
   * the display say "the mission is over there" about a rock with copper in it.
   */
  | 'mineral';

export interface Contact {
  x: number;
  y: number;
  kind: ContactKind;
  /** 1 → fresh, decays to 0 (PRD §14: echoes fade). */
  strength: number;
  label?: string;
  /**
   * A second line for the anchored callout — what the deposit IS, as against
   * what it is called. Only ore sets it; everything else is a mark and a name.
   */
  sub?: string;
  /**
   * A CSS colour this echo paints in, overriding the palette for its kind.
   *
   * Only ore uses it, and it is worth the field: eight minerals with eight
   * authored colours means a ping can say WHICH seam it found before the
   * player is close enough to read the label off it. A violet mark at the edge
   * of the display is abyssal crystal and nothing else in the game is.
   */
  colour?: string;
  /** The same colour as 0..1 floats, for the GPU text batch. */
  rgb?: [number, number, number];
  /**
   * The contact's world Z, when it has one worth knowing.
   *
   * The 2D display never needed it — a mark on a strip is an x and a y. A
   * WORLD-ANCHORED callout does: a deposit sits anywhere from z -7 to +1.5 and
   * further again on a background lane, and anchoring the label at z 0 instead
   * projects it through a different point in space from the rock it is
   * pointing at. That is a leader line ending in open water, which is exactly
   * what it drew.
   */
  z?: number;
  /**
   * Seconds this echo lasts, when it should not be `SONAR.contactLife`.
   *
   * A bogey's mark decays because the bogey MOVED — the echo is a position
   * that is going stale, and painting it after it stops being true is worse
   * than not painting it. A seam does not move. Nothing about an ore contact
   * is less true thirty seconds later than it was on the ping, so the reason
   * for the short life simply does not apply to it, and a survey you have to
   * re-ping every six seconds to keep reading is a survey nobody uses.
   */
  life?: number;
}

/**
 * Everything about a contact past its position and kind.
 *
 * An object rather than a tail of optional positional arguments: it had grown
 * to eight and `z` would have been the ninth, at which point a call site
 * getting two of them the wrong way round is a matter of time and would fail
 * silently — a colour in the `sub` slot is a string either way.
 */
export interface ContactOpts {
  label?: string;
  sub?: string;
  colour?: string;
  rgb?: [number, number, number];
  life?: number;
  z?: number;
}

export interface Ping {
  x: number;
  y: number;
  r: number;
  age: number;
  life: number;
}

export class Sonar {
  pings: Ping[] = [];
  contacts: Contact[] = [];
  cooldown = 0;
  /** How loud the player currently is, for enemy perception. */
  lastPingAt = -999;

  /** The single ping the shaders visualise — the newest, strongest one. */
  get primary(): Ping | null {
    let best: Ping | null = null;
    for (const p of this.pings) if (!best || p.age < best.age) best = p;
    return best;
  }

  canPing(energy: number) {
    return this.cooldown <= 0 && energy >= SONAR.cost;
  }

  /** `range` comes from the sonar upgrade; it is how far the shell gets. */
  emit(x: number, y: number, time: number, range: number = SONAR.maxRadius) {
    this.pings.push({ x, y, r: 0, age: 0, life: range / SONAR.speed });
    this.cooldown = SONAR.cooldown;
    this.lastPingAt = time;
  }

  /**
   * Returns true when this echo is NEW rather than a refresh of one already on
   * the display. Callers that announce a contact need that: a ping shell is
   * several frames wide, so the same seam is found on each of them, and
   * without it every deposit would fire its callout half a dozen times.
   */
  addContact(x: number, y: number, kind: ContactKind,
             opts: ContactOpts = {}): boolean {
    const { label, sub, colour, rgb, life, z } = opts;
    // Merge with a nearby existing echo instead of stacking duplicates.
    for (const c of this.contacts) {
      if (c.kind === kind && hyp(c.x - x, c.y - y) < 6) {
        c.x = x; c.y = y; c.strength = 1;
        // The label can change without the echo being new — a deposit read at
        // the edge of one ping and the middle of the next is the same contact
        // with a better description.
        if (label !== undefined) c.label = label;
        if (sub !== undefined) c.sub = sub;
        if (life !== undefined) c.life = life;
        if (rgb !== undefined) c.rgb = rgb;
        if (z !== undefined) c.z = z;
        return false;
      }
    }
    this.contacts.push({ x, y, kind, strength: 1, label, colour, sub, life, rgb, z });
    return true;
  }

  update(dt: number) {
    this.cooldown = Math.max(0, this.cooldown - dt);

    for (let i = this.pings.length - 1; i >= 0; i--) {
      const p = this.pings[i];
      p.age += dt;
      p.r = p.age * SONAR.speed;
      if (p.age > p.life) this.pings.splice(i, 1);
    }

    for (let i = this.contacts.length - 1; i >= 0; i--) {
      const c = this.contacts[i];
      c.strength -= dt / (c.life ?? SONAR.contactLife);
      if (c.strength <= 0) this.contacts.splice(i, 1);
    }
  }

  /** Normalised brightness of the ping shell for the shaders (0 when idle). */
  shellStrength(): number {
    const p = this.primary;
    if (!p) return 0;
    const t = p.age / p.life;
    return (1 - t) * (1 - t) * 1.15;
  }
}
